import React, { useContext, useEffect, useMemo, useState } from "react";
import Sorter from "components/ui/Table/Sorter";
import Loading from "../Loading";
import PaginationBar from "./PaginationBar";
import Loader from "./Loader";
import { DEFAULT_PAGE_SIZE } from "./paging";
import EmptyState from "./EmptyState";
import { ListCardContext, TableCardHeaderRow } from "./TableCardHeader";

// The one table for every list screen in the app.
//
// It started as the ChequeGuard table. The module lists (Customers, Invoices,
// Branches ... 61 screens) used to render components/shared/DataTable.js, a
// react-table + redux table, and CrmDashboard's Leads drew its own. All of them
// render this one now, so there is a single place for the card, header, rows,
// empty state, loading and pagination.
//
// Two column shapes are accepted, so no screen had to rewrite its columns:
//
//   ChequeGuard shape   { key, header, render(row), align, className, noCapitalize }
//   react-table shape   { Header, accessor, Cell, id, sortable }
//
// and two prop shapes:
//
//   ChequeGuard lists   rows, paging, onPageChange (+ title/subtitle/toolbar)
//   module lists        data, pagingData, onPaginationChange, onSort
//
// Module lists paint their title + search into this card from their
// <XxxTableTools> through a portal slot (see TableCardHeader.js); a table that
// has no title of its own opens that slot, exactly like the old table did.

// `table-default` (assets/styles/components/_tables.css) is kept on the table:
// several module screens tune their header through `.table-default thead ...`
// rules. Its header rule also sets text-align, which beats a utility class, so
// a right-aligned header gets its alignment inline.
const HEADER_CLASS =
  "px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider";

const cellAlign = (align) => (align === "right" ? "text-right" : "text-left");

const isReactTableColumn = (col) =>
  "Header" in col || "accessor" in col || "Cell" in col;

// react-table's id rule: explicit id, else a string accessor, else a string Header.
const reactTableId = (col, index) => {
  if (col.id) return col.id;
  if (typeof col.accessor === "string") return col.accessor;
  if (typeof col.Header === "string" && col.Header) return col.Header;
  return `col_${index}`;
};

// String accessors may be dotted paths ("client.name"), like react-table allowed.
const accessValue = (col, row, index, rows) => {
  if (typeof col.accessor === "function") {
    return col.accessor(row, index, undefined, undefined, rows);
  }
  if (typeof col.accessor === "string") {
    try {
      return col.accessor.split(".").reduce((cursor, part) => cursor[part], row);
    } catch (e) {
      return undefined;
    }
  }
  return undefined;
};

// react-table rendered Header and Cell as components (<Cell {...props} />), not
// as plain function calls, so a Cell may use hooks. Keep rendering them that way.
const isComponent = (thing) =>
  typeof thing === "function" ||
  (typeof thing === "object" &&
    thing !== null &&
    ["react.memo", "react.forward_ref"].includes(thing.$$typeof?.description));

const renderAsComponent = (Comp, props) =>
  isComponent(Comp) ? <Comp {...props} /> : Comp;

// react-table's own default Cell, for columns that do not define one.
const DefaultCell = ({ value = "" }) => value;

const toColumn = (col, index) => {
  if (!isReactTableColumn(col)) {
    return {
      id: col.key ?? `col_${index}`,
      header: col.header,
      headerStyle: col.align === "right" ? { textAlign: "right" } : undefined,
      sortable: Boolean(col.sortable),
      cellClassName: `px-6 py-4 whitespace-nowrap text-sm ${cellAlign(col.align)} ${
        col.noCapitalize ? "" : "capitalize"
      } ${col.className ?? "text-gray-500"}`,
      renderCell: (row) => (col.render ? col.render(row) : row[col.key] ?? "-"),
    };
  }

  const id = reactTableId(col, index);
  const column = { ...col, id };

  return {
    id,
    // react-table fell back to a non-breaking space when a column had no Header.
    header: col.Header === undefined ? String.fromCharCode(160) : renderAsComponent(col.Header, { column }),
    headerStyle: undefined,
    sortable: Boolean(col.sortable),
    // No classes: these cells keep the look they had (.table-default padding,
    // inherited text), so e-mail addresses are not capitalised and long text wraps.
    cellClassName: undefined,
    renderCell: (row, rowIndex, rows) => {
      const value = accessValue(col, row, rowIndex, rows);
      const rowMeta = { original: row, index: rowIndex, id: String(rowIndex) };
      return renderAsComponent(col.Cell === undefined ? DefaultCell : col.Cell, {
        value,
        row: rowMeta,
        column,
        cell: { value, row: rowMeta, column },
        data: rows,
      });
    },
  };
};

const DataTable = ({
  title,
  subtitle,
  columns = [],
  rows: rowsProp,
  data,
  rowKey,
  loading = false,
  emptyText = "No records found",
  emptyHint,
  emptyAction,
  paging,
  pagingData,
  onPageChange,
  onPaginationChange,
  onSort,
  toolbar,

  groupBy,
}) => {
  // Module lists pass `data` + `pagingData`; ChequeGuard lists pass `rows` + `paging`.
  const moduleList = rowsProp === undefined && data !== undefined;
  const rows = (moduleList ? data : rowsProp) || [];

  let totalCount;
  let pageSize;
  let currentPage;
  let totalPages;
  if (moduleList) {
    // Same maths as the old module table: no pagingData means no footer.
    const pd = pagingData || {};
    totalCount = Number(pd.total) || 0;
    pageSize = pd.pageSize || 10;
    currentPage = pd.pageIndex ?? 1;
    totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  } else {
    totalCount = paging?.totalCount ?? rows.length ?? 0;
    pageSize = paging?.pageSize ?? DEFAULT_PAGE_SIZE;
    currentPage = paging?.currentPage ?? 1;
    totalPages = paging?.totalPages ?? Math.max(1, Math.ceil(totalCount / pageSize));
  }

  const cols = useMemo(() => columns.map(toColumn), [columns]);

  const hasOwnHeader = Boolean(title || subtitle || toolbar);

  // Card header slot + item count for a module's <TableCardHeader>.
  const listCard = useContext(ListCardContext);
  const registerSlot = hasOwnHeader ? undefined : listCard?.registerSlot;
  const setListTotal = hasOwnHeader ? undefined : listCard?.setTotal;
  useEffect(() => {
    setListTotal?.(totalCount);
  }, [setListTotal, totalCount]);

  // Server-side sorting. The first click on a column asks for "asc", the next
  // for "desc", and so on, which is the order the react-table version sent.
  const [sortBy, setSortBy] = useState(null);
  const handleSort = (col) => {
    if (loading) return;
    const order = sortBy?.id === col.id && sortBy.order === "asc" ? "desc" : "asc";
    setSortBy({ id: col.id, order });
    onSort?.({ order, key: col.id }, { id: col.id, clearSortBy: () => setSortBy(null) });
  };

  // A module list's page request is ignored while its last one is in flight,
  // like the old table did (its redux fetches have no stale-response guard).
  const handlePageChange = moduleList
    ? (page) => {
        if (!loading) onPaginationChange?.(page);
      }
    : onPageChange;

  return (
    <div className="w-full min-w-0 bg-white border rounded-lg shadow-sm overflow-hidden">
      {hasOwnHeader ? (
        <TableCardHeaderRow
          title={title}
          subtitle={subtitle}
          count={loading ? <Loader inline text="Loading..." /> : `${totalCount} items`}
        >
          {toolbar}
        </TableCardHeaderRow>
      ) : (
        registerSlot && <div ref={registerSlot} />
      )}

      <Loading loading={loading && rows.length > 0} type="cover">
        <div className="overflow-x-auto">
          <table className="table-default min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {cols.map((col, i) => (
                  <th key={`${col.id}_${i}`} className={HEADER_CLASS} style={col.headerStyle}>
                    {col.header ? (
                      col.sortable ? (
                        <div className="cursor-pointer" onClick={() => handleSort(col)}>
                          {col.header}
                          <span>
                            <Sorter
                              sort={sortBy?.id === col.id ? sortBy.order === "desc" : undefined}
                            />
                          </span>
                        </div>
                      ) : (
                        <div>{col.header}</div>
                      )
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={cols.length || 1} className="px-6 py-12 text-center">
                    {loading ? (
                      <div className="flex justify-center">
                        <Loader inline text="Loading..." />
                      </div>
                    ) : (
                      <EmptyState message={emptyText} hint={emptyHint} action={emptyAction} />
                    )}
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => {
                  const startsGroup =
                    groupBy &&
                    (index === 0 || groupBy.key(row) !== groupBy.key(rows[index - 1]));

                  return (
                    <React.Fragment key={rowKey ? rowKey(row) : index}>
                      {startsGroup && (
                        <tr className="bg-gray-100">
                          <td
                            colSpan={cols.length}
                            className="px-6 py-2 text-xs font-semibold uppercase tracking-wider text-gray-600"
                          >
                            {groupBy.label(row)}
                          </td>
                        </tr>
                      )}
                      <tr className="hover:bg-gray-50">
                        {cols.map((col, i) => (
                          <td key={`${col.id}_${i}`} className={col.cellClassName}>
                            {col.renderCell(row, index, rows)}
                          </td>
                        ))}
                      </tr>
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Loading>

      <PaginationBar
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        shownCount={rows.length}
        itemLabel="items"
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default DataTable;
